import {createAction} from '@reduxjs/toolkit';
import {To} from 'history';
import React from 'react';
import {Route, Routes, useNavigate} from 'react-router';
import {DashboardCategory} from '~/entities/dashboard-categories';
import {SuspenseAndErrorBoundary} from '~/entities/fallbacks';
import {VersionUpdateFloatingPane} from '~/entities/versions';
import {HomePageLoadingScreen} from '~/processes/dashboard/HomePageLoadingScreen';
import {HomePageBanner} from '~/processes/dashboard/components/HomePageBanner/HomePageBanner';
import {HomePageModalsQueue} from '~/processes/dashboard/components/HomePageModalsQueue';
import HomePageActionsButtonGroup from './components/HomePageActionsButtonGroup/HomePageActionsButtonGroup';
import {TotalBalanceToast} from './components/TotalBalance';
import {HomeDashboardLayout, HomeLinksLayout, HomePageLayout, HomeToastsLayout} from './components/layouts';

export interface HomePagePaths {
    toReceiveCurrencySelection: To;
    toExchangeCurrencySelection: To;
    toSendCurrencySelection: To;
}

interface HomePageProps {
    paths: HomePagePaths;

    categories: DashboardCategory[];
    alerts?: React.JSX.Element;
    useTotalBalance: () => string;
}

export function HomePage(props: HomePageProps) {
    return (
        <Routes>
            <Route
                index
                element={
                    <SuspenseAndErrorBoundary Fallback={HomePageLoadingScreen}>
                        <DashboardHomeScreen {...props} />
                    </SuspenseAndErrorBoundary>
                }
            />
            {props.categories.map(c => (
                <Route
                    key={c.categorySlug}
                    path={`${c.categorySlug}/*`}
                    element={
                        <c.CategoryContextProvider>
                            <c.CategoryPage />
                        </c.CategoryContextProvider>
                    }
                />
            ))}
        </Routes>
    );
}

export const refreshHomePage = createAction('app/refreshHomePage');

function DashboardHomeScreen({categories, paths, alerts, useTotalBalance}: HomePageProps) {
    const navigate = useNavigate();
    const BalancesConsumer = DashboardCategory.useBalancesConsumer(categories);

    return (
        <>
            <HomePageLayout>
                <HomeDashboardLayout>
                    <HomeToastsLayout>{alerts}</HomeToastsLayout>

                    <BalancesConsumer>
                        {portions => <TotalBalanceToast useBalance={useTotalBalance} portions={portions} />}
                    </BalancesConsumer>

                    <HomePageBanner />

                    <HomePageActionsButtonGroup
                        onReceiveClick={() => navigate(paths.toReceiveCurrencySelection)}
                        onExchangeClick={() => navigate(paths.toExchangeCurrencySelection)}
                        onSendClick={() => navigate(paths.toSendCurrencySelection)}
                    />
                </HomeDashboardLayout>

                <HomeLinksLayout>
                    {categories.map(c => (
                        <c.CategoryContextProvider key={c.categorySlug}>
                            <c.CategoryLink to={c.categorySlug} />
                        </c.CategoryContextProvider>
                    ))}
                </HomeLinksLayout>

                <HomePageModalsQueue />
            </HomePageLayout>
            <VersionUpdateFloatingPane />
        </>
    );
}
